import {makeAutoObservable, observable} from 'mobx'
import React from 'react'
import {FilenameUtil, logger, Util} from 'yougile-util'
import {Hub} from 'yougile-model'
import {ClientUtilities} from '@client/client-utilities'
import {Backend} from '@common/backend'
import {createUseStore} from '@containers/hooks'
import {FileViewData} from '@extensions/message-utils'

export interface FileView extends FileViewData {
  index: number
}

type RefZoomType = React.MutableRefObject<RefZoom | undefined> | undefined

export interface RefZoom {
  zoomOut: (n: number) => void
  zoomIn: (n: number) => void
  reset: () => void
  ref: {
    current: {
      clientHeight: number
      clientWidth: number
    }
  }
}

interface State {
  autoplay: boolean
  files: FileView[]
  currentFileIndex: number
  currentFile: FileView | undefined
  image: HTMLImageElement
  imageLoad: boolean
  imageLoadError: boolean
  video: HTMLVideoElement
  videoLoad: boolean
  videoLoadError: boolean
  zoom: number
  refZoom: RefZoomType
  onClose: () => void
  hub?: Hub
}

export class FileViewerStore {
  state: State = {
    autoplay: false,
    files: [],
    currentFileIndex: 0,
    currentFile: undefined,
    image: new Image(),
    imageLoad: false,
    imageLoadError: false,
    video: document.createElement('video'),
    videoLoad: false,
    videoLoadError: false,
    zoom: 1,
    refZoom: undefined,
    onClose: () => null
  }

  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  setFiles(files: FileViewData[]): void {
    this.state.files = files.map((data, index) => ({...data, index}))
  }

  setCurrentFileIndex(index: number): void {
    this.state.currentFileIndex = index
  }

  setHub(hub: Hub | undefined): void {
    this.state.hub = hub
  }

  next(): void {
    if (this.canGoNext) {
      this.state.currentFileIndex += 1
    }
  }

  prev(): void {
    if (this.canGoPrev) {
      this.state.currentFileIndex -= 1
    }
  }

  setImageLoad(imageLoad: boolean, imageLoadError: boolean): void {
    this.state.imageLoadError = imageLoadError
    this.state.imageLoad = imageLoad
  }

  setVideoLoad(videoLoad: boolean, videoLoadError: boolean): void {
    this.state.videoLoadError = videoLoadError
    this.state.videoLoad = videoLoad
  }

  loadImage(src: string): void {
    this.setImageLoad(true, false)
    this.state.image.onload = () => {
      this.setImageLoad(false, false)
    }
    this.state.image.onerror = () => {
      this.setImageLoad(false, true)
    }
    this.state.video.onreset = () => {
      this.setImageLoad(false, false)
    }
    this.state.image.src = Util.isDesktop() ? Backend.toDataUrl(src) : src
  }

  loadVideo(file: FileView): void {
    this.setImageLoad(true, false)
    this.state.video.onloadeddata = () => {
      this.setImageLoad(false, false)
    }
    this.state.video.onerror = e => {
      this.setImageLoad(false, true)
      logger.log('Preview: failed to load video', e, new Error().stack)
    }
    this.state.video.onreset = () => {
      this.setImageLoad(false, false)
    }
    if (file.file) {
      this.state.video.src = file.url
    } else {
      const src = file.url
      this.state.video.src = Util.isDesktop() ? Backend.toDataUrl(src) : src
    }
  }

  resetLoad(): void {
    this.setImageLoad(false, false)
    this.setVideoLoad(false, false)
  }

  setCurrentFile(file: FileView): void {
    this.state.currentFile = file
  }

  setZoom(zoom: number): void {
    this.state.zoom = zoom
  }

  setRefZoom(ref: RefZoomType): void {
    this.state.refZoom = ref
  }

  setOnClose(onClose: () => void): void {
    this.state.onClose = onClose
  }

  get name(): string {
    if (this.state.currentFile?.file?.name) {
      return this.state.currentFile.file.name
    }
    const file = this.state.currentFile?.url || 'not found'
    const name = FilenameUtil.getFilename(file)
    return name
  }

  get canGoNext(): boolean {
    return this.state.currentFileIndex !== this.state.files.length - 1
  }

  get canGoPrev(): boolean {
    return this.state.currentFileIndex > 0
  }

  setAsCover(): void {
    const originalUrl = this.state.currentFile?.url
    const hub = this.state.hub
    if (hub && originalUrl) {
      void ClientUtilities.createPreviewForServerImage(
        originalUrl,
        {height: 160, width: 800},
        previewUrl => {
          if (hub && previewUrl) {
            hub.data.image = previewUrl
            hub.commit()
          }
        }
      )
    }
  }
}

const [FileViewerContext, useFileViewerStore] = createUseStore(FileViewerStore)
export {FileViewerContext, useFileViewerStore}
